import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Divider,
    LinearProgress,
    Chip
} from '@mui/material'
import {
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Person as PersonIcon,
    Settings as SettingsIcon,
    CalendarToday as CalendarIcon,
    Build as BuildIcon
} from '@mui/icons-material'
import { formatDateWithTime } from '../utils/DateUtils'

const ReportConfirmDialog = ({ open, handleClose, handleConfirm, handleReplace, handleRestore, existingReport, currentReport, mode = 'create', loading = false }) => {
    if (!existingReport) return null

    const isWorking = existingReport.state === 'En proceso' || existingReport.level > 0
    const progress = existingReport.progress || 0

    const getCreatorName = (r) => {
        if (!r) return 'S/I'
        if (r.isAutomatic) return 'Sistema (Sincronización API)'

        const createdBy = r.createdBy
        if (!createdBy) return 'Sistema'

        // Si está poblado como objeto
        if (typeof createdBy === 'object') {
            const roleDbName = typeof createdBy.role === 'object' ? createdBy.role?.dbName : createdBy.role
            if (roleDbName === 'superAdmin') return 'Sistema'
            if (createdBy.name || createdBy.lastName) {
                return `${createdBy.name || ''} ${createdBy.lastName || ''}`.trim()
            }
            return 'Sistema'
        }

        // Si es solo un ID (string)
        if (createdBy === 'superAdmin') return 'Sistema'
        return 'Sistema'
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: isWorking ? '#fff3e0' : '#fff', borderBottom: '1px solid #eee' }}>
                <WarningIcon color="warning" />
                <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                    ID SAP en uso: {existingReport.sapId}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                        label={existingReport.deleted ? 'En Papelera' : 'En Sistema'}
                        color={existingReport.deleted ? 'error' : 'success'}
                        size="small"
                        variant="filled"
                        sx={{ fontWeight: 'bold', color: '#fff' }}
                    />
                    <Chip
                        label={existingReport.state}
                        color={isWorking ? 'warning' : 'default'}
                        size="small"
                        variant="outlined"
                    />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
                <DialogContentText sx={{ mb: 3 }}>
                    {existingReport.deleted
                        ? `Se ha detectado que la OT ${existingReport.newIdIndex || existingReport.idIndex} con ID SAP "${existingReport.sapId}" se encuentra eliminada en el sistema. Puede reestablecerla para volver a utilizarla.`
                        : 'Se ha detectado que el ID SAP ingresado ya pertenece a un reporte activo en el sistema.'}
                </DialogContentText>

                <Grid container spacing={3}>
                    {/* Columna Izquierda: Antes (Reporte Existente) */}
                    <Grid item xs={12} md={currentReport ? 6 : 12}>
                        <Box sx={{ bgcolor: '#fff5f5', p: 2, borderRadius: 2, height: '100%', border: '1px solid #ffcdd2', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, fontWeight: 'bold' }}>
                                <WarningIcon fontSize="small" /> ANTES: Reporte Existente
                            </Typography>

                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">N° Orden (OT)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{existingReport.newIdIndex || existingReport.idIndex}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">ID SAP</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{existingReport.sapId || 'N/A'}</Typography>
                                </Grid>
                            </Grid>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">Sitio / Obra</Typography>
                                <Typography variant="body2">{existingReport.obra?.idObra} - {existingReport.obra?.descripcion || existingReport.site}</Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">Máquina</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {existingReport.maquina?.equ || existingReport.maquina?.equid || existingReport.machine}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {existingReport.maquina ? `${existingReport.maquina.brand || ''} ${existingReport.maquina.model || ''} (${existingReport.maquina.type || 'S/T'})`.trim() : ''}
                                </Typography>
                            </Box>

                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Pauta / Guía</Typography>
                                    <Typography variant="body2">{existingReport.guide}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Tipo</Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{existingReport.reportType}</Typography>
                                </Grid>
                            </Grid>

                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Creado por</Typography>
                                    <Typography variant="body2">{getCreatorName(existingReport)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Fecha Creación</Typography>
                                    <Typography variant="body2">{formatDateWithTime(existingReport.createdAt)}</Typography>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 'auto', pt: 2 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Progreso</span>
                                    <span>{progress}%</span>
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{ height: 6, borderRadius: 5, mt: 0.5 }}
                                    color={progress === 100 ? 'success' : 'primary'}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* Columna Derecha: Después (Reporte Actual) */}
                    {currentReport && (
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#e8f5e9', p: 2, borderRadius: 2, height: '100%', border: '1px solid #c8e6c9', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, fontWeight: 'bold' }}>
                                    <InfoIcon fontSize="small" color="success" /> DESPUÉS: Reporte Actual
                                </Typography>

                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">N° Orden (OT)</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{currentReport.newIdIndex || currentReport.idIndex}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">ID SAP</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{currentReport.sapId || 'N/A'}</Typography>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="textSecondary" display="block">Sitio / Obra</Typography>
                                    <Typography variant="body2">{currentReport.obra?.idObra || ''} - {currentReport.obra?.descripcion || currentReport.site}</Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" color="textSecondary" display="block">Máquina</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {currentReport.maquina?.equ || currentReport.maquina?.equid || currentReport.machine}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {currentReport.maquina ? `${currentReport.maquina.brand || ''} ${currentReport.maquina.model || ''} (${currentReport.maquina.type || 'S/T'})`.trim() : ''}
                                    </Typography>
                                </Box>

                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Pauta / Guía</Typography>
                                        <Typography variant="body2">{currentReport.guide}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Tipo</Typography>
                                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{currentReport.reportType}</Typography>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Creado por</Typography>
                                        <Typography variant="body2">{getCreatorName(currentReport)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Fecha Creación</Typography>
                                        <Typography variant="body2">{formatDateWithTime(currentReport.createdAt)}</Typography>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 'auto', pt: 2 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Progreso</span>
                                        <span>{currentReport.progress || 0}%</span>
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={currentReport.progress || 0}
                                        sx={{ height: 6, borderRadius: 5, mt: 0.5 }}
                                        color={(currentReport.progress || 0) === 100 ? 'success' : 'primary'}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {isWorking && !existingReport.deleted && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#fff4e5', borderRadius: 2, border: '1px solid #ffa726', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <ErrorIcon color="warning" sx={{ mt: 0.5 }} />
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#663c00' }}>
                                ¡Atención! El reporte existente ya tiene actividad registrada.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#663c00' }}>
                                {mode === 'edit'
                                    ? `Si confirma el reemplazo, el reporte OT ${existingReport.newIdIndex || existingReport.idIndex} será anulado (marcado como borrado) y se reemplazará por este. El progreso de ${progress}% se perderá.`
                                    : `Si confirma el reemplazo, el reporte OT ${existingReport.newIdIndex || existingReport.idIndex} será anulado (marcado como borrado) y se reemplazará por uno nuevo. El progreso de ${progress}% se perderá.`}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Typography variant="body2" align="center" sx={{ mt: 3, fontWeight: 'medium' }}>
                    {existingReport.deleted
                        ? `¿Desea restaurar la OT ${existingReport.newIdIndex || existingReport.idIndex} con sus datos originales o reemplazarla por una nueva?`
                        : (mode === 'edit'
                            ? '¿Está seguro de que desea reemplazar el Reporte Existente por Reporte Actual y continuar?'
                            : '¿Está seguro de que desea anular el reporte existente y reemplazarlo por uno nuevo?')}
                </Typography>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid #eee', gap: 1 }}>
                <Button onClick={handleClose} color="inherit" variant="outlined" sx={{ borderRadius: 10, px: 3 }} disabled={loading}>
                    Cancelar
                </Button>
                {existingReport.deleted ? (
                    <>
                        <Button
                            onClick={handleRestore || handleConfirm}
                            color="success"
                            variant="contained"
                            disabled={loading}
                            sx={{ borderRadius: 10, px: 3, fontWeight: 'bold', boxShadow: 'none', '&:hover': { boxShadow: '0 4px 8px rgba(46, 125, 50, 0.4)' } }}
                        >
                            {loading ? 'Procesando...' : 'Restaurar'}
                        </Button>
                        <Button
                            onClick={handleReplace || handleConfirm}
                            color="error"
                            variant="contained"
                            disabled={loading}
                            sx={{ borderRadius: 10, px: 3, fontWeight: 'bold', boxShadow: 'none' }}
                        >
                            {loading ? 'Procesando...' : 'Reemplazar'}
                        </Button>
                    </>
                ) : (
                    <Button
                        onClick={handleConfirm || handleReplace}
                        color="error"
                        variant="contained"
                        disabled={loading}
                        sx={{ borderRadius: 10, px: 4, fontWeight: 'bold', boxShadow: 'none' }}
                    >
                        {loading ? 'Procesando...' : (mode === 'edit' ? 'Confirmar y Reemplazar' : 'Reemplazar')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ReportConfirmDialog
